import { Module } from "../../../lib/ui/Module.js";

export class ProjectEditor extends Module {

    constructor(parent, options = {}) {

        options = Object.assign({
            moduleName: 'ProjectEditor',
            moduleTitle: 'Project Editor',
        }, options ?? {});

        super(parent, options);

        this.workspaceManager = null;
        this.workspaceProject = null;
        this.projectDetails = null;
        this.modelSections = [];
        this.loadError = null;
        this.isLoading = false;

    }

    getWorkspaceManager() {

        if (!this.workspaceManager)
            this.workspaceManager = this.getServices().getWorkspaceManager();

        return this.workspaceManager;

    }

    getRouteParams(activeRoute = null) {

        return activeRoute?.params || this.getApp()?.getActiveRouteParams() || {};

    }

    async _refresh(options, activeRoute) {

        await super._refresh(options, activeRoute);

        const { workspaceUid, projectUid } = this.getRouteParams(activeRoute);
        await this.loadProject(workspaceUid, projectUid);

    }

    async loadProject(workspaceUid, projectUid) {

        if (!workspaceUid || !projectUid)
            throw new Error('Workspace UID and project UID are required.');

        this.isLoading = true;
        this.loadError = null;

        try {
            const workspaceProject = await this.getWorkspaceManager().getWorkspaceProject(workspaceUid, projectUid);

            if (!workspaceProject)
                throw new Error('Projekt neexistuje nebo k němu nemáte oprávnění.');

            this.workspaceProject = workspaceProject;

            const [projectDetails, modelSectionsDataset] = await Promise.all([
                this.getWorkspaceManager().getWorkspaceProjectDetails(workspaceUid, projectUid),
                this.getWorkspaceManager().getAllWorkspaceProjectModelSections(workspaceUid, projectUid),
            ]);

            this.projectDetails = projectDetails;
            this.modelSections = Array.isArray(modelSectionsDataset?.items) ? modelSectionsDataset.items : [];
        } catch (error) {
            this.workspaceProject = null;
            this.projectDetails = null;
            this.modelSections = [];
            this.loadError = error;
        } finally {
            this.isLoading = false;
        }

    }

    openModelSection(modelSection) {

        const { workspaceUid, projectUid } = this.getRouteParams();
        const modelSectionUid = modelSection?.uid || null;

        if (!workspaceUid || !projectUid || !modelSectionUid)
            return;

        this.getApp().routeTo('workspace-project-model-section-editor', { workspaceUid, projectUid, modelSectionUid });

    }

    _render(r, vm) {

        const editor = this;
        const projectTitle = editor.projectDetails?.name
            || editor.workspaceProject?.projectName
            || editor.workspaceProject?.projectLabel
            || 'Project Editor';

        return r.module(null, [
            r.header(null, [
                r.nav(null, [
                    r.iconButton('#back', { css: 'transparent circle', props: { type: 'button', onclick: () => editor.getApp().navigateBack() } }),
                    r.h4([projectTitle]),
                ]),
            ]),
            r.section(null, [
                renderContent(),
            ]),
        ]);

        function renderContent() {

            if (editor.isLoading)
                return r.p(['Načítám projekt...']);

            if (editor.loadError)
                return r.p('error', [editor.getLoadErrorMessage(editor.loadError)]);

            if (!editor.workspaceProject)
                return r.p(['Projekt není načtený.']);

            return [
                r.table({ css: 'lines' }, [
                    r.tbody([
                        r.trow([
                            r.tcell('bold', ['Workspace']),
                            r.tcell([editor.workspaceProject.workspaceLabel || editor.workspaceProject.workspaceUid || '']),
                        ]),
                        r.trow([
                            r.tcell('bold', ['Projekt']),
                            r.tcell([projectTitle]),
                        ]),
                        r.trow([
                            r.tcell('bold', ['Role']),
                            r.tcell([editor.workspaceProject.userRoleLabel || editor.workspaceProject.userRoleCode || '']),
                        ]),
                    ]),
                ]),
                renderModelSections(),
            ];

        }

        function renderModelSections() {

            if (!editor.modelSections.length)
                return r.p(['Projekt zatím neobsahuje žádné model sections.']);

            return r.table({ css: 'lines hover' }, [
                r.thead([
                    r.trow([
                        r.thcell(['Model section']),
                        r.thcell('min', ['Akce']),
                    ]),
                ]),
                r.tbody(editor.modelSections.map(modelSection => r.trow([
                    r.tcell([
                        r.span({
                            css: 'pointer-cursor',
                            props: {
                                onclick: () => editor.openModelSection(modelSection),
                            }
                        }, [
                            modelSection.name || modelSection.uid || 'Model section'
                        ]),
                    ]),
                    r.tcell('min', [
                        r.iconButton('#open', {
                            css: 'circle transparent',
                            props: {
                                type: 'button',
                                disabled: !modelSection.uid,
                                onclick: () => editor.openModelSection(modelSection),
                            },
                        }),
                    ]),
                ]))),
            ]);

        }

    }

    getLoadErrorMessage(error) {

        const code = error?.code ?? error?.details?.code;
        if (code === 403000100 || code === 404000100 || code === 404020001)
            return 'Projekt neexistuje nebo k němu nemáte oprávnění.';

        return error?.details?.message || error?.message || 'Projekt se nepodařilo načíst.';

    }

}
